import { ServerEvent } from "@utilitydelta/eventplanedb-client";
import ProjectWorld, { createServerEvent, ProjectEventType, type ColoredSquare } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";

export const MINOR_VERSION: number = 1;

export interface SetColor_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.SetColor;
    x: number;
    y: number;
    color: number;
}

export interface SetColor_ProjectionResult extends BaseProjectionResult {
    kind: 'SetColor_ProjectionResult';
    square: ColoredSquare; // The affected square
    oldColor: number; // Previous color value
}

export interface SetColor_EventInputs extends BaseEventInput {
    kind: 'SetColor_EventInputs';
    x: number;
    y: number;
    color: number;
}

export type SetColor_EventProcessorType = EventProcessor<ProjectEventType, SetColor_EventInputs, SetColor_LocalEvent, SetColor_ProjectionResult, SetColor_LocalEvent, ServerEvent<ProjectEventType>>;

export class SetColor_EventProcessor implements SetColor_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }

    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.SetColor; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.SetColor; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    public createUndoEvent = (undoEvent: SetColor_LocalEvent, originalEventProjectionResult: SetColor_ProjectionResult): boolean => {
        undoEvent.x = originalEventProjectionResult.square.x;
        undoEvent.y = originalEventProjectionResult.square.y;
        undoEvent.color = originalEventProjectionResult.oldColor;
        return true;
    }

    public projectOntoWorld = (eventToProject: SetColor_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): SetColor_ProjectionResult | undefined => {

        const coloredsquareId = `${eventToProject.x}_${eventToProject.y}`;
        let entity = this.projectWorld.coloredSquares.maybeGet(coloredsquareId);

        let oldColor: number | undefined = undefined;

        if (entity === undefined) {
            // Create new entity
            entity = {
                id: coloredsquareId,
                isDeleted: false,
                pi: this.projectWorld.projectMetadata.id,
                lastEventTime: { [eventToProject.tp]: eventToProject.et } as Record<ProjectEventType, number>,
                createdByClientId: eventToProject.ci,
                createdByUserId: eventToProject.ui,
                createdOn: eventToProject.et,
                modifiedByClientId: eventToProject.ci,
                modifiedByUserId: eventToProject.ui,
                modifiedOn: eventToProject.et,
                x: eventToProject.x,
                y: eventToProject.y,
                color: eventToProject.color,
            };
            this.projectWorld.coloredSquares.upsert(entity);
        } else {
            // Verify this event is newer than the last processed event of this type
            // This concurrency control ensures a consistent world model even when clients are offline
            if (entity.lastEventTime[eventToProject.tp] !== undefined && entity.lastEventTime[eventToProject.tp] > eventToProject.et) {
                return undefined;
            }

            if (entity.color === eventToProject.color) return undefined;

            // Update entity fields based on event data
            oldColor = entity.color;
            entity.color = eventToProject.color;

            // Update metadata
            entity.lastEventTime[eventToProject.tp] = eventToProject.et;
            entity.modifiedByClientId = eventToProject.ci;
            entity.modifiedByUserId = eventToProject.ui;
            entity.modifiedOn = eventToProject.et;
        }

        this.processProjectUser(eventToProject, addUpdatedEntitiesToDirtySet);

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('coloredSquares', entity);
        }
        return !returnProjectionResults ? undefined : {
            kind: 'SetColor_ProjectionResult',
            square: entity,
            oldColor: oldColor ?? 0,
        };
    }

    private processProjectUser = (event: SetColor_LocalEvent, addUpdatedEntitiesToDirtySet: boolean): void => {
        if (event.color === 0) return;

        const id = event.ui ?? event.ci;
        if (!id) return;

        const projectUser = this.projectWorld.projectUsers.maybeGetIgnoreDeleted(id);
        if (!projectUser) return;

        // Verify this event is newer than the last processed event of this type
        if (projectUser.lastEventTime[event.tp] !== undefined && projectUser.lastEventTime[event.tp] > event.et) {
            return undefined;
        }

        projectUser.color = event.color;

        // Update metadata
        projectUser.lastEventTime[event.tp] = event.et;
        projectUser.modifiedByClientId = event.ci;
        projectUser.modifiedByUserId = event.ui;
        projectUser.modifiedOn = event.et;

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('projectUsers', projectUser);
        }
    }

    public createLocalEvent = (eventToPopulate: SetColor_LocalEvent, userInputs: SetColor_EventInputs): boolean => {
        const colorToUse = userInputs.color;
        const clearOperation = colorToUse === undefined;
        const square = this.projectWorld.coloredSquares.maybeGet(`${userInputs.x}_${userInputs.y}`);

        if (square === undefined && clearOperation) return false;
        if (square !== undefined && square.color === colorToUse) return false;

        eventToPopulate.x = userInputs.x;
        eventToPopulate.y = userInputs.y;
        eventToPopulate.color = userInputs.color;

        return true;
    }

    public toServerEvent = async (localEvent: SetColor_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            x: localEvent.x,
            y: localEvent.y,
            color: localEvent.color
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: SetColor_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}